import React from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { isNullOrWhitespace } from "@utils/utility";
import { getDisplayedAmount } from "@utils";
import AccountStatementBetDetailsError from "./AccountStatementBetDetailsError";
import AccountStatementBetDetailsOffer from "./AccountStatementBetDetailsOffer";
import { prepareOfferData } from "@utils/account-statement/AccountStatementUtils";
import { FiscalizationSection } from "@components/fiscalization/desktop/FiscalizationSection";
import { useAccountStatementStore } from "@state/hooks/account-statement/useAccountStatementStore";
import { Header } from "./Header";
import { AdditionalDescriptionSystem } from "./AdditionalDescriptionSystem";
import { AdditionalDescriptionNotSystem } from "./AdditionalDescriptionNotSystem";
import { HandlingFeePercentage } from "./HandlingFeePercentage";
import { AdditionalGain } from "./AdditionalGain";
import { AdditionalGainBonus } from "./AdditionalGainBonus";
import { AdditionalGainTax } from "./AdditionalGainTax";
import { AdditionalPayout } from "./AdditionalPayout";

export default function AccountStatementBetDetails() {
	const t = useTranslation("mybets").t;
	const store = useAccountStatementStore();

	if (
		store.accountStatementDetailsStore.slipDetails == null ||
		!("betSlipNumber" in store.accountStatementDetailsStore.slipDetails)
	) {
		return (
			<AccountStatementBetDetailsError
				setDetailsType={
					store.accountStatementDetailsStore.setDetailsType
				}
			/>
		);
	}

	const {
		isLive,
		system,
		totalNumberOfCombinations,
		dateCreated,
		payment,
		maxGain,
		eventCount,
		paymentPerCombination,
		maxCoefficient,
	} = store.accountStatementDetailsStore.slipDetails;

	const isSystem = !isNullOrWhitespace(system);

	const finalOffers = prepareOfferData(
		store.accountStatementDetailsStore.slipDetails
	);

	return (
		<div className="popup">
			<div className="popup__card popup__card--reset-width popup--account-statement__card">
				<Header
					slipDetails={store.accountStatementDetailsStore.slipDetails}
				/>

				<div className="popup__body">
					<div className="card--bet">
						<table className="table--bets">
							<thead className="table--bets__head">
								<tr className="table--bets__head__row">
									<th
										className="u-padd--reset u-type--wgt--medium"
										colSpan={8}
									>
										<table
											className="table--bets--reset"
											cellSpacing="0"
											cellPadding="0"
										>
											<tbody>
												<tr>
													<th className="table--bets__th table--bets__th--time">
														<span>
															{t<string>(
																"ACC_STATEMENT.BET_DETAILS.INFO_DATE"
															)}
														</span>
														<span>
															{DateTime.fromISO(
																dateCreated
															).toLocaleString(
																DateTime.DATETIME_SHORT_WITH_SECONDS
															)}
														</span>
													</th>
													<th className="table--bets__th table--bets__th--type">
														<span>
															{t(
																"mybets:ITEM.PICK"
															)}
														</span>

														<span>
															<AdditionalDescriptionSystem
																slipDetails={
																	store
																		.accountStatementDetailsStore
																		.slipDetails
																}
															/>
														</span>

														<span>
															<AdditionalDescriptionNotSystem
																slipDetails={
																	store
																		.accountStatementDetailsStore
																		.slipDetails
																}
															/>
														</span>
													</th>
													<th className="table--bets__th table--bets__th--price">
														<span>
															{t<string>(
																"ACC_STATEMENT.BET_DETAILS.INFO_PAYMENT"
															)}
														</span>
														<span className="u-type--wgt--bold">
															{getDisplayedAmount(
																payment
															)}
														</span>
													</th>
													<th className="table--bets__th table--bets__th--price">
														<span>
															{t<string>(
																"ACC_STATEMENT.BET_DETAILS.INFO_MAX_GAIN"
															)}
														</span>
														<span className="u-type--wgt--bold">
															{getDisplayedAmount(
																maxGain
															)}
														</span>
													</th>
												</tr>
											</tbody>
										</table>
									</th>
								</tr>
								<tr className="table--bets__subhead">
									<th className="table--bets__ts table--bets__ts--time">
										{t<string>(
											"ACC_STATEMENT.BET_DETAILS.T_TIME"
										)}
									</th>
									<th className="table--bets__ts table--bets__ts--combi">
										&nbsp;
									</th>
									<th className="table--bets__ts table--bets__ts--event">
										{t<string>(
											"ACC_STATEMENT.BET_DETAILS.T_EVENT"
										)}
									</th>
									<th className="table--bets__ts table--bets__ts--bank">
										&nbsp;
									</th>
									<th className="table--bets__ts table--bets__ts--bet-type">
										{t<string>(
											"ACC_STATEMENT.BET_DETAILS.T_TIP"
										)}
									</th>
									<th className="table--bets__ts table--bets__ts--odds">
										{t<string>(
											"ACC_STATEMENT.BET_DETAILS.T_QUOTA"
										)}
									</th>
									<th className="table--bets__ts table--bets__ts--results">
										{t<string>(
											"ACC_STATEMENT.BET_DETAILS.T_RESULT"
										)}
									</th>
									<th className="table--bets__ts table--bets__ts--icon">
										&nbsp;
									</th>
								</tr>
							</thead>
							<tbody className="table--bets__body">
								{finalOffers &&
									finalOffers.map((offer) => (
										<AccountStatementBetDetailsOffer
											offer={offer}
											isSystem={isSystem}
											totalCombinations={
												totalNumberOfCombinations
											}
											isLive={isLive}
											key={offer.bettingOfferId}
											status={status}
										/>
									))}

								<tr className="table--bets__details">
									<td className="u-padd--reset" colSpan={8}>
										<table className="table--bets--reset">
											<tbody>
												<tr className="table--bets__tr">
													<td className="table--bets__dd">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_NUM_SELECTED_EVENTS"
														)}
													</td>
													<td className="table--bets__dd">
														{eventCount}
													</td>
												</tr>
												<tr className="table--bets__tr">
													<td className="table--bets__dd">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_NUM_BETS"
														)}
													</td>
													<td className="table--bets__dd">
														{
															totalNumberOfCombinations
														}
													</td>
												</tr>
												<tr className="table--bets__tr">
													<td className="table--bets__dd">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_STAKE_PER_BET"
														)}
													</td>
													<td className="table--bets__dd">
														{getDisplayedAmount(
															paymentPerCombination
														)}
													</td>
												</tr>

												<tr className="table--bets__tr">
													<td className="table--bets__dd">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_TOTAL_STAKE"
														)}
													</td>
													<td className="table--bets__dd">
														{getDisplayedAmount(
															payment
														)}
													</td>
												</tr>

												<HandlingFeePercentage
													slipDetails={
														store
															.accountStatementDetailsStore
															.slipDetails
													}
												/>

												<tr className="table--bets__tr">
													<td className="table--bets__dd">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_MAX_COEFFICIENT"
														)}
													</td>
													<td className="table--bets__dd">
														{maxCoefficient}
													</td>
												</tr>

												<AdditionalGainBonus
													slipDetails={
														store
															.accountStatementDetailsStore
															.slipDetails
													}
												/>

												<AdditionalGainTax
													slipDetails={
														store
															.accountStatementDetailsStore
															.slipDetails
													}
												/>

												<tr className="table--bets__tr">
													<td className="table--bets__dd">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_MAX_GAIN"
														)}
													</td>
													<td className="table--bets__dd">
														{getDisplayedAmount(
															maxGain
														)}
													</td>
												</tr>

												<tr className="table--bets__tr">
													<td className="table--bets__dd u-color--sent--danger">
														{t<string>(
															"ACC_STATEMENT.BET_DETAILS.ADDITIONAL_PAYMENT"
														)}
													</td>
													<td className="table--bets__dd u-color--sent--danger">
														{getDisplayedAmount(
															payment
														)}
													</td>
												</tr>
												<AdditionalGain
													slipDetails={
														store
															.accountStatementDetailsStore
															.slipDetails
													}
												/>
												<AdditionalPayout
													slipDetails={
														store
															.accountStatementDetailsStore
															.slipDetails
													}
												/>
												<FiscalizationSection
													metadata={
														store
															.accountStatementDetailsStore
															.slipDetails
															.metadata
													}
												/>
											</tbody>
										</table>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
}
